// Advanced AI PRN Prediction Component
// Features: Historical Pattern Learning, Multi-Factor Forecasting, Real-time Learning

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Brain, TrendingUp, TrendingDown, Sparkles, RefreshCw, Target,
  AlertTriangle, CheckCircle2, Calendar, BarChart3, Lightbulb,
  ChevronRight, Activity, Zap, Database, Clock, DollarSign, Mail, Upload, Settings2
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import { useApiData } from '@/hooks';
import AdvancedTrainingAlerts from './AdvancedTrainingAlerts';
import CustomDatasetManager from './CustomDatasetManager';
import HyperparameterTuning from './HyperparameterTuning';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-bold mb-2">{label}</p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' 
              ? entry.name.includes('$') ? `$${entry.value.toLocaleString()}` : entry.value
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Seasonal Factors Chart
const SeasonalFactorsChart = ({ data }) => {
  const chartData = data?.seasonal_factors || [];
  
  return (
    <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
      <CardHeader>
        <CardTitle className="text-white font-bold flex items-center gap-2">
          <Calendar className="h-5 w-5 text-cyan-400" />
          Seasonal Impact Factors
        </CardTitle>
        <CardDescription className="text-slate-400">
          Monthly adjustment factors for PRN prediction (1.0 = baseline)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="month_name" stroke="#94a3b8" fontSize={10} angle={-45} textAnchor="end" height={60} />
              <YAxis stroke="#94a3b8" fontSize={12} domain={[0.8, 1.3]} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="factor" name="Factor" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.is_current ? '#06b6d4' : (entry.factor > 1.1 ? '#f59e0b' : '#10b981')} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Badge className="bg-cyan-500/20 text-cyan-400">Current Month</Badge>
          <Badge className="bg-amber-500/20 text-amber-400">High Demand ({'>'}10%)</Badge>
          <Badge className="bg-emerald-500/20 text-emerald-400">Normal/Low</Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Model Performance Panel
const ModelPerformancePanel = ({ data, loading }) => {
  if (loading) return <Skeleton className="h-96" />;
  
  if (data?.status === 'no_data') {
    return (
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardContent className="p-8 text-center">
          <Database className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <p className="text-slate-400">No prediction history available yet.</p>
          <p className="text-sm text-slate-500 mt-2">
            The model learns from actual vs predicted comparisons. Start recording actual PRN hours to enable learning.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const accuracy = data?.accuracy_metrics || {};
  const errors = data?.error_analysis || {};
  const insights = data?.learning_insights || [];
  
  return (
    <div className="space-y-4">
      {/* Accuracy Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <Target className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-emerald-400 font-bold">Overall Accuracy</p>
                <p className="text-3xl font-black text-white">{accuracy.overall_accuracy || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <Activity className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-cyan-400 font-bold">Recent Accuracy</p>
                <p className="text-3xl font-black text-white">{accuracy.recent_accuracy || 'N/A'}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <Database className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <p className="text-sm text-violet-400 font-bold">Predictions Made</p>
                <p className="text-3xl font-black text-white">{data?.predictions_recorded || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Bias Analysis */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-amber-400" />
            Error Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-slate-800/50">
              <p className="text-sm text-slate-400">Average Error</p>
              <p className={`text-2xl font-bold ${errors.avg_error_percent > 0 ? 'text-amber-400' : 'text-cyan-400'}`}>
                {errors.avg_error_percent > 0 ? '+' : ''}{errors.avg_error_percent || 0}%
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/50">
              <p className="text-sm text-slate-400">Model Bias</p>
              <Badge className={`text-lg ${
                errors.bias === 'well-calibrated' ? 'bg-emerald-500/20 text-emerald-400' :
                'bg-amber-500/20 text-amber-400'
              }`}>
                {errors.bias || 'unknown'}
              </Badge>
            </div>
            <div className="p-4 rounded-lg bg-slate-800/50">
              <p className="text-sm text-slate-400">Std Deviation</p>
              <p className="text-2xl font-bold text-white">{errors.std_deviation || 0}%</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Learning Insights */}
      {insights.length > 0 && (
        <Card className="bg-gradient-to-br from-violet-900/30 to-purple-900/30 border border-violet-500/30">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-violet-400" />
              Learning Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {insights.map((insight, idx) => (
                <li key={idx} className="flex items-start gap-2 text-slate-300">
                  <ChevronRight className="h-4 w-4 text-violet-400 mt-1 flex-shrink-0" />
                  {insight}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Multi-Factor Forecast Display
const MultiFatorForecastPanel = ({ data, loading }) => {
  if (loading) return <Skeleton className="h-96" />;
  
  const forecasts = data?.forecasts || [];
  const summary = data?.summary || {};
  const factors = data?.factors_summary || {};
  
  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-amber-400 font-bold">Avg Weekly Hours</p>
            <p className="text-3xl font-black text-white">{summary.avg_weekly_hours || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-cyan-400 font-bold">Total Shifts (4wk)</p>
            <p className="text-3xl font-black text-white">{summary.total_shifts_needed || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-violet-400 font-bold">Monthly Cost</p>
            <p className="text-3xl font-black text-white">${(summary.monthly_cost_estimate || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-emerald-400 font-bold">Model Confidence</p>
            <p className="text-3xl font-black text-white">{Math.round((summary.model_confidence || 0) * 100)}%</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Factor Summary */}
      <Alert className="bg-cyan-900/20 border-cyan-500/30">
        <Brain className="h-4 w-4 text-cyan-400" />
        <AlertTitle className="text-cyan-400 font-bold">Multi-Factor Analysis</AlertTitle>
        <AlertDescription className="text-cyan-200 text-sm">
          <div className="flex flex-wrap gap-4 mt-2">
            <span>Primary: {factors.primary_driver}</span>
            <span>|</span>
            <span>Seasonal: {factors.seasonal_impact}</span>
            <span>|</span>
            <span>Census: {factors.census_trend}</span>
          </div>
        </AlertDescription>
      </Alert>
      
      {/* Weekly Forecasts with Confidence Intervals */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-400" />
            Weekly Forecasts with Confidence Intervals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-2 text-slate-300 font-bold">Week</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Predicted Hours</th>
                  <th className="text-center py-3 px-2 text-slate-300 font-bold">Confidence Range</th>
                  <th className="text-center py-3 px-2 text-slate-300 font-bold">Shifts</th>
                  <th className="text-center py-3 px-2 text-slate-300 font-bold">Confidence</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Combined Factor</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.map((forecast, idx) => (
                  <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-3 px-2 text-white font-medium">{forecast.week}</td>
                    <td className="py-3 px-2 text-right text-amber-400 font-bold">{forecast.predicted_hours}</td>
                    <td className="py-3 px-2 text-center text-slate-400">
                      {forecast.confidence_interval?.low} - {forecast.confidence_interval?.high}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <Badge className="bg-amber-500/20 text-amber-400">{forecast.shifts_needed}</Badge>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <Badge className={
                        forecast.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                        forecast.confidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-rose-500/20 text-rose-400'
                      }>
                        {forecast.confidence} ({forecast.confidence_score}%)
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-right text-cyan-400">{forecast.factors?.combined?.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Forecast Chart */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold">Forecast Visualization</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={forecasts}>
                <defs>
                  <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="predicted_hours" 
                  name="Predicted Hours"
                  stroke="#f59e0b" 
                  fill="url(#forecastGradient)" 
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Record Actual Hours Form
const RecordActualHoursForm = ({ token, onRecorded }) => {
  const [week, setWeek] = useState(new Date().toISOString().split('T')[0]);
  const [actualHours, setActualHours] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!actualHours) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/advanced-ai/record-actual`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          week,
          actual_hours: parseInt(actualHours)
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Recorded! Model accuracy: ${data.accuracy}%`);
        setActualHours('');
        onRecorded?.();
      } else {
        toast.error('Failed to record hours');
      }
    } catch (error) {
      toast.error('Error recording hours');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
      <CardHeader>
        <CardTitle className="text-white font-bold flex items-center gap-2">
          <Clock className="h-5 w-5 text-cyan-400" />
          Record Actual PRN Hours
        </CardTitle>
        <CardDescription className="text-slate-400">
          Train the AI model by recording actual hours used each week
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[150px]">
            <Label className="text-slate-300">Week Starting</Label>
            <Input
              type="date"
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <Label className="text-slate-300">Actual PRN Hours</Label>
            <Input
              type="number"
              value={actualHours}
              onChange={(e) => setActualHours(e.target.value)}
              placeholder="e.g., 24"
              min="0"
              step="8"
              className="bg-slate-800 border-slate-700 text-white"
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading || !actualHours}
            className="bg-cyan-500 hover:bg-cyan-600"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Record Hours
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Main Advanced AI Component
export default function AdvancedAIPrediction({ token }) {
  const [activeTab, setActiveTab] = useState('forecast');
  const [enhancedPrediction, setEnhancedPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);
  
  // Fetch data
  const { data: multiFactorData, loading: multiFactorLoading, refetch: refetchForecast } = useApiData(
    '/api/labor-utilization/advanced-ai/multi-factor-forecast?forecast_weeks=4'
  );
  
  const { data: modelPerf, loading: modelPerfLoading, refetch: refetchModelPerf } = useApiData(
    '/api/labor-utilization/advanced-ai/model-performance'
  );
  
  const { data: seasonalData, loading: seasonalLoading } = useApiData(
    '/api/labor-utilization/advanced-ai/seasonal-factors'
  );
  
  const { data: historicalPatterns, loading: patternsLoading } = useApiData(
    '/api/labor-utilization/advanced-ai/historical-patterns?weeks_back=52'
  );
  
  // Generate enhanced AI prediction
  const generateEnhancedPrediction = async () => {
    setPredicting(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/advanced-ai/enhanced-prediction?forecast_weeks=4`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setEnhancedPrediction(data);
        toast.success('AI prediction generated');
      }
    } catch (error) {
      toast.error('Failed to generate prediction');
    } finally {
      setPredicting(false);
    }
  };
  
  const handleRecorded = () => {
    refetchModelPerf();
    refetchForecast();
  };
  
  return (
    <div className="space-y-6" data-testid="advanced-ai-prediction">
      {/* Header */}
      <Alert className="bg-gradient-to-r from-violet-900/40 to-purple-900/40 border-violet-500/30">
        <Brain className="h-4 w-4 text-violet-400" />
        <AlertTitle className="text-violet-400 font-bold">Advanced AI PRN Prediction Model v2.0</AlertTitle>
        <AlertDescription className="text-violet-200 text-sm">
          Multi-factor forecasting with historical pattern learning, seasonal adjustments, and real-time model training.
        </AlertDescription>
      </Alert>
      
      {/* Generate Enhanced Prediction Button */}
      <div className="flex justify-end">
        <Button 
          onClick={generateEnhancedPrediction}
          disabled={predicting}
          className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 gap-2"
        >
          {predicting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Generate AI Enhanced Prediction
        </Button>
      </div>
      
      {/* Enhanced Prediction Results */}
      {enhancedPrediction && (
        <Card className="bg-gradient-to-br from-violet-900/30 to-purple-900/30 border border-violet-500/30">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Brain className="h-5 w-5 text-violet-400" />
              AI Enhanced Prediction (Model v{enhancedPrediction.model_version})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <Alert className="bg-slate-800/50 border-violet-500/30">
              <Lightbulb className="h-4 w-4 text-violet-400" />
              <AlertDescription className="text-slate-200">
                {enhancedPrediction.prediction?.summary}
              </AlertDescription>
            </Alert>
            
            {/* Model Insights */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-xs text-slate-400">Learning Status</p>
                <Badge className={enhancedPrediction.model_insights?.learning_status === 'active' 
                  ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                  {enhancedPrediction.model_insights?.learning_status || 'no_data'}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-xs text-slate-400">Accuracy Score</p>
                <p className="text-lg font-bold text-white">
                  {enhancedPrediction.model_insights?.accuracy_score || 'N/A'}%
                </p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-xs text-slate-400">Bias Status</p>
                <Badge className={enhancedPrediction.model_insights?.bias_detected === 'well-calibrated'
                  ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                  {enhancedPrediction.model_insights?.bias_detected || 'unknown'}
                </Badge>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-xs text-slate-400">Data Quality</p>
                <p className="text-lg font-bold text-white">
                  {Math.round((enhancedPrediction.data_quality?.score || 0) * 100)}%
                </p>
              </div>
            </div>
            
            {/* Recommendations */}
            {enhancedPrediction.prediction?.recommendations && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <Target className="h-4 w-4 text-cyan-400" />
                  Recommendations
                </h4>
                <ul className="space-y-1">
                  {enhancedPrediction.prediction.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                      <ChevronRight className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Optimization Opportunities */}
            {enhancedPrediction.prediction?.optimization_opportunities && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <Zap className="h-4 w-4 text-emerald-400" />
                  Cost Optimization Opportunities
                </h4>
                <ul className="space-y-1">
                  {enhancedPrediction.prediction.optimization_opportunities.map((opt, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-emerald-300">
                      <DollarSign className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {opt}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Tabs for Different Views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="forecast" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <TrendingUp className="h-4 w-4" /> Multi-Factor Forecast
          </TabsTrigger>
          <TabsTrigger value="performance" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Activity className="h-4 w-4" /> Model Performance
          </TabsTrigger>
          <TabsTrigger value="seasonal" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Calendar className="h-4 w-4" /> Seasonal Factors
          </TabsTrigger>
          <TabsTrigger value="hyperparameters" data-testid="hyperparameters-tab" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Settings2 className="h-4 w-4" /> Hyperparameters
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Database className="h-4 w-4" /> Train Model
          </TabsTrigger>
          <TabsTrigger value="custom-datasets" data-testid="custom-datasets-tab" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Upload className="h-4 w-4" /> Custom Datasets
          </TabsTrigger>
          <TabsTrigger value="training-alerts" data-testid="training-alerts-tab" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-rose-500 data-[state=active]:to-pink-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Mail className="h-4 w-4" /> Training & Alerts
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="forecast" className="mt-6">
          <MultiFatorForecastPanel data={multiFactorData} loading={multiFactorLoading} />
        </TabsContent>
        
        <TabsContent value="performance" className="mt-6">
          <ModelPerformancePanel data={modelPerf} loading={modelPerfLoading} />
        </TabsContent>
        
        <TabsContent value="seasonal" className="mt-6">
          <SeasonalFactorsChart data={seasonalData} loading={seasonalLoading} />
        </TabsContent>
        
        <TabsContent value="hyperparameters" className="mt-6" data-testid="hyperparameters-content">
          <HyperparameterTuning token={token} />
        </TabsContent>
        
        <TabsContent value="training" className="mt-6">
          <div className="space-y-6">
            <RecordActualHoursForm token={token} onRecorded={handleRecorded} />
            
            {/* Historical Patterns */}
            {!patternsLoading && historicalPatterns?.patterns && (
              <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white font-bold flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-cyan-400" />
                    Historical Patterns Detected
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400 mb-2">Over-Capacity Pattern</p>
                      <p className="text-lg font-bold text-white">
                        Mean: {historicalPatterns.patterns.over_capacity?.mean?.toFixed(1) || 0} visits
                      </p>
                      <Badge className={
                        historicalPatterns.patterns.over_capacity?.trend === 'increasing' ? 'bg-rose-500/20 text-rose-400' :
                        historicalPatterns.patterns.over_capacity?.trend === 'decreasing' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-slate-500/20 text-slate-400'
                      }>
                        {historicalPatterns.patterns.over_capacity?.trend || 'stable'}
                      </Badge>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400 mb-2">Admission Pattern</p>
                      <p className="text-lg font-bold text-white">
                        Avg: {historicalPatterns.patterns.admissions?.weekly_avg?.toFixed(1) || 0}/week
                      </p>
                      <Badge className={
                        historicalPatterns.patterns.admissions?.trend === 'increasing' ? 'bg-emerald-500/20 text-emerald-400' :
                        historicalPatterns.patterns.admissions?.trend === 'decreasing' ? 'bg-rose-500/20 text-rose-400' :
                        'bg-slate-500/20 text-slate-400'
                      }>
                        {historicalPatterns.patterns.admissions?.trend || 'stable'}
                      </Badge>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400 mb-2">Census Pattern</p>
                      <p className="text-lg font-bold text-white">
                        Current: {historicalPatterns.patterns.census?.current || 0}
                      </p>
                      <Badge className={
                        historicalPatterns.patterns.census?.trend === 'increasing' ? 'bg-emerald-500/20 text-emerald-400' :
                        historicalPatterns.patterns.census?.trend === 'decreasing' ? 'bg-rose-500/20 text-rose-400' :
                        'bg-slate-500/20 text-slate-400'
                      }>
                        {historicalPatterns.patterns.census?.trend || 'stable'} ({historicalPatterns.patterns.census?.growth_rate?.toFixed(1) || 0}%/wk)
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                    <p className="text-sm text-cyan-400">
                      <strong>Data Quality Score:</strong> {Math.round((historicalPatterns.patterns.data_quality_score || 0) * 100)}% 
                      ({historicalPatterns.patterns.weeks_analyzed || 0} weeks analyzed)
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="custom-datasets" className="mt-6" data-testid="custom-datasets-content">
          <CustomDatasetManager token={token} />
        </TabsContent>
        
        <TabsContent value="training-alerts" className="mt-6" data-testid="training-alerts-content">
          <AdvancedTrainingAlerts token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
